import type { UbaProduct } from '../types'

import {
    keepPreviousData,
    useQuery,
    useQueryClient
} from '@tanstack/react-query'
import bytes from 'bytes'
import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { MdAttachFile } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import Select from 'react-select'

import http from '../../../lib/http'
import { useAppDispatch } from '../../../store'
import * as previewerActions from '../../../store/modules/previewer'
import { PDFIcon } from '../../chat/components/icons/PDFIcon'
import { useGlobalLoadingIndicator } from '../../global-loading-indicator'
import { NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { MaterialDeleteButton } from '../../shared/material-delete-button'
import { formatterPrice, useUbaPlaceOrderMutation } from '../queries'
import { counsellingOptions, placeOrderSchema } from '../types'

import { ErrorHandler } from './error-handler'
// import { Checkbox } from '../../shared/NarusMaterial'

interface ShippingDetails {
    salesChannelName: string
    shippingComment: string
    shippingDescription: string
    shippingDetail: string
    shippingId: number
    shippingPrice: string
    shippingType: string
}

function useShippingDetails({ productIds }: { productIds: number[] }) {
    return useQuery({
        queryKey: ['shippingDetails', productIds],
        queryFn: async ({ queryKey }) => {
            const [, productIds] = queryKey
            const { data } = await http.postRequest<{
                data: ShippingDetails[]
            }>('/uba/get-shipping-details', {
                productIds,
                countryCode: 'US'
            })
            return data
        },
        enabled: !!productIds.length,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData
    })
}

const FILE_5_MB = 5e6

interface Props {
    products: Array<UbaProduct & { quantity: number }>
    patientGUID: string
    cleanUp: () => void
}

function getFormattedDate(date: Date): string {
    let year = date.getFullYear()
    let month = (1 + date.getMonth()).toString().padStart(2, '0')
    let day = date.getDate().toString().padStart(2, '0')

    return `${month}/${day}/${year}`
}

export function OrderTotal({
    products,
    patientGUID,
    cleanUp
}: Props): JSX.Element {
    const dispatch = useAppDispatch()
    const { id } = useParams()
    const queryClient = useQueryClient()
    const globalLoader = useGlobalLoadingIndicator('place-order')
    const [state, setState] = useState<{
        counselling: { label: string; value: number } | undefined | null
        comment: string
        couponCode: string
    }>({ counselling: counsellingOptions[1], comment: '', couponCode: '' })
    const [shipping, setShipping] = useState<ShippingDetails | null>(null)
    const [addRemoteDataLogger, setAddRemoteDataLogger] = useState(false)
    const { data: shippingDetailsData, isFetched } = useShippingDetails({
        productIds: products.map((p) => p.productID)
    })
    const [latestError, setLatestError] = useState(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [files, setFiles] = useState<Array<{ file: File; preview: string }>>()

    let createMutation = useUbaPlaceOrderMutation()

    useEffect(() => {
        setShipping(null)
    }, [products.length])

    const subtotal = products.reduce(
        (acc, p) => Number(p.price ?? 0) * p.quantity + acc,
        0
    )

    const shippingPrice = Number(shipping?.shippingPrice)

    const sum = !!products.length && shippingPrice + subtotal

    const showControls = !!products.length

    const shippingQuantity = 1

    function handlePlaceOrder(): void {
        const data = {
            patientId: id,
            patientGUID,
            products: products.map((product) => ({
                productId: product.productID,
                productName: product.productName,
                manufacturer: product.manufacturer,
                price: product.price,
                quantity: product.quantity,
                dinNumber: product.dinNumber
            })),
            shippingId: shipping?.shippingId,
            shippingQuantity: 1,
            counselling: state.counselling?.value,
            orderComment: state.comment,
            couponCode: state.couponCode,
            orderDate: getFormattedDate(new Date()),
            addRemoteDataLogger
        }

        const result = placeOrderSchema.safeParse(data)

        if (result.success) {
            globalLoader.startLoading('Placing Order')
            return createMutation.mutate(
                { ...result.data, files: files?.map((f) => f.file) ?? [] },
                {
                    async onError(e: any) {
                        toast.error('Error while placing order', {
                            position: 'bottom-center'
                        })
                        setLatestError(
                            e?.response?.data ?? { message: e?.message }
                        )
                    },
                    onSettled() {
                        globalLoader.finishLoading()
                    },
                    onSuccess() {
                        toast.success('Order Placed', {
                            position: 'bottom-center'
                        })
                        cleanUp()
                        queryClient
                            .invalidateQueries({ queryKey: ['ubaOrders', id] })
                            .catch((e) => {
                                console.error(e)
                            })
                        setShipping(null)
                        setFiles([])
                        setLatestError(null)
                        setAddRemoteDataLogger(false)
                        setState({
                            counselling: counsellingOptions[1],
                            comment: '',
                            couponCode: ''
                        })
                    }
                }
            )
        }
        toast.error(
            `Validation error while placing order: ${
                result.error.errors[0]?.path.join('.') ?? ''
            } - ${result?.error?.errors?.[0]?.message ?? ''}`,
            { position: 'bottom-center' }
        )
        console.error(result.error)
    }

    function removeImage(name: string): void {
        setFiles((prev) => prev?.filter((p) => p.file.name !== name))
    }

    return (
        <div className="flex flex-col justify-between bg-white rounded-lg p-4 h-fit min-h-[calc(50vh)]">
            {!showControls && (
                <NoDataPlaceHolder title="Add products to see shipping methods" />
            )}

            {!shippingDetailsData?.length && showControls && isFetched && (
                <NoDataPlaceHolder title={"Can't find any shipping method"} />
            )}

            <div className="flex flex-col gap-2 mb-2">
                {showControls &&
                    shippingDetailsData?.map((p, index) => {
                        const price = formatterPrice
                            .format(Number(p.shippingPrice) * shippingQuantity)
                            .replace('.00', '')
                        return (
                            <button
                                key={`${p.shippingId}-${index}`}
                                className={cx(
                                    'w-full box-border border-1 border-solid flex flex-col gap-2 rounded-lg p-5 relative transition-colors cursor-pointer',
                                    [
                                        shipping?.shippingId === p.shippingId &&
                                            'border-blue-500 bg-blue-50',
                                        shipping?.shippingId !== p.shippingId &&
                                            'border-zinc-200 hover:bg-blue-50'
                                    ]
                                )}
                                onClick={() => setShipping(p)}
                            >
                                <div className="flex flex-row gap-2 justify-between items-start mb-2">
                                    <p
                                        title={`sales channel name: ${p.salesChannelName}`}
                                        className="text-base"
                                    >
                                        {p.shippingDescription}
                                    </p>
                                    <p className="text-lg">{price}</p>
                                </div>
                                {!!p.shippingComment.length && (
                                    <p className="text-zinc-800">
                                        {p.shippingComment}
                                    </p>
                                )}
                                <p className="text-zinc-800">
                                    {p.shippingDetail}
                                </p>
                            </button>
                        )
                    })}
            </div>

            {showControls && (
                <>
                    <hr className="my-6" />
                    <div className="flex flex-col gap-3 mt-2 mb-2">
                        {files?.map((f) => {
                            return (
                                <button
                                    key={f.file.name}
                                    className="flex flex-row gap-2 items-center cursor-pointer"
                                    onClick={() => {
                                        dispatch(
                                            previewerActions.open({
                                                mimeType: f.file.type,
                                                name: f.file.name,
                                                data: f.preview
                                            })
                                        )
                                    }}
                                >
                                    {f.file.type === 'application/pdf' ? (
                                        <div className="w-[50px] h-[50px] flex justify-center items-center">
                                            <PDFIcon />
                                        </div>
                                    ) : (
                                        <img
                                            title={f.file.name}
                                            src={f.preview}
                                            alt=""
                                            width={50}
                                            height={50}
                                        />
                                    )}
                                    <div className="flex flex-col gap-1 justify-center">
                                        <p>{f.file.name}</p>
                                        <p>{bytes(f.file.size)}</p>
                                    </div>

                                    <div className="flex-1 text-end">
                                        <MaterialDeleteButton
                                            inTable={false}
                                            onClick={() =>
                                                removeImage(f.file.name)
                                            }
                                        />
                                    </div>
                                </button>
                            )
                        })}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg, image/png, application/pdf"
                            className="hidden"
                            hidden
                            multiple
                            onChange={(event) => {
                                const files = event.target.files ?? []
                                const filesArray = Array.from(files)

                                for (const file of filesArray) {
                                    if (file.size > FILE_5_MB) {
                                        toast.error(
                                            `${file.name} more than 5 megabytes`,
                                            { position: 'bottom-center' }
                                        )
                                        continue
                                    }

                                    const reader = new FileReader()
                                    reader.onload = (event) => {
                                        setFiles((prev) => [
                                            ...(prev ?? []),
                                            {
                                                file,
                                                preview: event.target
                                                    ?.result as string
                                            }
                                        ])
                                    }

                                    reader.readAsDataURL(file)
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-solid border-zinc-100 bg-zinc-100 rounded-md py-2 text-zinc-800 text-sm text-center disabled:bg-zinc-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <MdAttachFile />
                            Prescription files
                        </button>
                        <Select
                            options={counsellingOptions}
                            value={counsellingOptions.find(
                                (o) => o.value === state.counselling?.value
                            )}
                            required
                            className="relative z-1000"
                            classNames={{
                                control: (state) => {
                                    return state.isFocused
                                        ? 'outline outline-2 outline-primary'
                                        : 'outline-none'
                                }
                            }}
                            styles={{
                                control: (styles: any) => ({
                                    ...styles,
                                    backgroundColor: '#F4F4F5',
                                    border: '1px solid transparent',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        border: '1px solid #DADCE0'
                                    },
                                    borderRadius: '4px',
                                    height: '2.6rem',
                                    outline: '1px'
                                }),
                                container: (styles: any, { isFocused }) => ({
                                    ...styles,
                                    flex: 1,
                                    backgroundColor: '#F4F4F5',
                                    boxShadow: 'none',
                                    borderRadius: '8px',
                                    zIndex: isFocused ? 10 : 5,
                                    outlineColor: 'transparent!important'
                                }),
                                indicatorSeparator: (styles: any) => ({
                                    ...styles,
                                    marginBottom: 0,
                                    marginTop: 0
                                }),
                                dropdownIndicator: (styles: any) => ({
                                    ...styles,
                                    padding: '0 18px'
                                })
                            }}
                            onChange={(value) =>
                                setState((prev) => ({
                                    ...prev,
                                    counselling: value
                                }))
                            }
                        />
                        <input
                            onChange={(e) =>
                                setState((prev) => ({
                                    ...prev,
                                    couponCode: e.target.value
                                }))
                            }
                            value={state.couponCode}
                            className="border-2 border-solid border-zinc-100 bg-zinc-100 rounded-md py-3 px-4 hover:border-zinc-200 focus:border-[#0165FF] transition-colors"
                            type="text"
                            placeholder="Coupon Code"
                        />
                        <textarea
                            onChange={(e) =>
                                setState((prev) => ({
                                    ...prev,
                                    comment: e.target.value
                                }))
                            }
                            value={state.comment}
                            className="outline-none resize-none border-2 border-solid border-zinc-100 bg-zinc-100 rounded-md py-3 px-4 hover:border-zinc-200 focus:border-[#0165FF] transition-colors"
                            placeholder="Comment"
                        />

                        {/*<Checkbox*/}
                        {/*  id='add-remote-data-logger'*/}
                        {/*  label='Add Remote Data Logger'*/}
                        {/*  value={addRemoteDataLogger}*/}
                        {/*  onChange={(value) => setAddRemoteDataLogger(value)}*/}
                        {/*/>*/}

                        {createMutation.isError && (
                            <ErrorHandler error={latestError} />
                        )}
                    </div>

                    <hr className="my-6" />

                    <div className="grid grid-cols-3 pb-6 gap-y-1 text-center">
                        <p className="text-xs text-zinc-500">Subtotal</p>
                        <p className="text-xs text-zinc-500">Shipping</p>
                        <p className="text-xs text-zinc-500">Total</p>

                        <p className="text-lg">
                            {formatterPrice.format(subtotal)}
                        </p>

                        {!isNaN(shippingPrice) && (
                            <p className="text-lg">
                                {formatterPrice.format(shippingPrice)}
                            </p>
                        )}

                        {!!sum && !isNaN(sum) && (
                            <p className="text-lg">
                                {formatterPrice.format(Number(sum))}
                            </p>
                        )}
                    </div>

                    <button
                        disabled={
                            !shipping?.shippingId || createMutation.isPending
                        }
                        className="py-3 px-4 text-center bg-[#2f7de1] text-white rounded-md disabled:bg-zinc-200 disabled:cursor-not-allowed"
                        onClick={handlePlaceOrder}
                    >
                        Place Order {sum ? formatterPrice.format(sum) : ''}
                    </button>
                </>
            )}
        </div>
    )
}
